'use client';
import { ExtendedTestimonial } from "@/data/testimonials";
import { X } from "lucide-react";
import Image from "next/image";
import { FaStar, FaStarHalfAlt } from "react-icons/fa";
import Button from "./Button";

interface TestimonialModalProps {
  testimonial: ExtendedTestimonial | null;
  isOpen: boolean;
  onClose: () => void;
}

const TestimonialModal: React.FC<TestimonialModalProps> = ({
  testimonial,
  isOpen,
  onClose,
}) => {
  if (!isOpen || !testimonial) return null;

  // Render stars based on rating (supports half stars) - Professional style
  const renderStars = (rating: number) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

    return (
      <div className="flex items-center gap-0.5">
        {[...Array(fullStars)].map((_, idx) => (
          <FaStar key={`full-${idx}`} className="text-yellow-400 w-5 h-5" fill="currentColor" />
        ))}
        {hasHalfStar && (
          <FaStarHalfAlt className="text-yellow-400 w-5 h-5" fill="currentColor" />
        )}
        {[...Array(emptyStars)].map((_, idx) => (
          <FaStar key={`empty-${idx}`} className="text-gray-300 w-5 h-5" fill="currentColor" />
        ))}
      </div>
    );
  };

  return (
    <div
      className={`fixed inset-0 z-[99999] flex items-center justify-center transition-opacity duration-300 ${isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300"
        onClick={onClose}
      />

      {/* Modal Content */}
      <div
        className={`relative z-[100000] w-[92%] max-w-4xl bg-white shadow-xl rounded-lg overflow-hidden transition-all duration-300 ${isOpen ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-2 scale-95"
          }`}
      >
        {/* Close Button - Top Right */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-white hover:bg-gray-50 text-gray-500 hover:text-gray-900 shadow-md border border-gray-200 transition-all duration-200"
          aria-label="Close modal"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Top Section */}
        <div className="px-8 pt-10 pb-8">
          <div className="flex items-start justify-between gap-6">
            {/* Left: Client Info */}
            <div className="space-y-1 flex-1">
              <p className="text-2xl font-bold text-[#1b1b1b] leading-tight">
                By {testimonial.clientName}, {testimonial.clientDesignation}
              </p>
              <p className="text-2xl font-semibold text-[#1b1b1b] leading-tight">
                {testimonial.clientCompany}
              </p>
            </div>

            {/* Right: Company Logo */}
            <div className="w-40 h-20 relative flex-shrink-0">
              <Image
                src={testimonial.clientCompanyLogo}
                alt={testimonial.clientCompany}
                fill
                className="object-contain"
                sizes="160px"
              />
            </div>
          </div>
        </div>

        {/* Center Section: Professional Review Card */}
        <div className="px-8 py-6">
          <div
            className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden select-none"
            style={{
              userSelect: 'none',
              WebkitUserSelect: 'none',
              MozUserSelect: 'none',
              msUserSelect: 'none'
            }}
            onCopy={(e) => e.preventDefault()}
            onCut={(e) => e.preventDefault()}
            onPaste={(e) => e.preventDefault()}
            onContextMenu={(e) => e.preventDefault()}
          >
            {/* Review Header - Professional Style */}
            <div className="bg-gray-50/80 px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between flex-wrap gap-4">
                {/* Left: Rating Section */}
                <div className="flex items-center gap-3">
                  {renderStars(testimonial.rating)}
                  <div className="flex items-baseline gap-1">
                    <span className="text-xl font-bold text-[#1b1b1b]">
                      {testimonial.rating.toFixed(1)}
                    </span>
                    <span className="text-sm text-gray-500 font-medium">/ 5</span>
                  </div>
                </div>

                {/* Right: Platform Logo */}
                {testimonial.platformLogo && (
                  <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-md border border-gray-200">
                    <span className="text-xs text-gray-500 uppercase tracking-wide font-medium">Verified on</span>
                    <div className="w-20 h-5 relative">
                      <Image
                        src={testimonial.platformLogo}
                        alt="Platform logo"
                        fill
                        className="object-contain"
                        sizes="80px"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Review Content */}
            <div className="px-6 py-7">
              <div className="relative select-none">
                {/* Review Text - Protected from copying */}
                <p
                  className="text-base text-gray-700 font-normal select-none"
                  style={{
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                    MozUserSelect: 'none',
                    msUserSelect: 'none',
                    WebkitTouchCallout: 'none',
                    pointerEvents: 'auto'
                  }}
                >
                  {testimonial.review}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="px-8 pb-10 pt-6 border-t border-gray-100">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            {/* Left: Client Data */}
            <div className="flex flex-col gap-2 text-base text-[#1b1b1b]">
              <p>
                <span className="font-semibold">Name:</span> {testimonial.clientName}
              </p>
              <p>
                <span className="font-semibold">Title:</span> {testimonial.clientDesignation}
              </p>
              <p className="font-medium">{testimonial.clientCompany}</p>
            </div>

            {/* Right: Check Project Button */}
            {testimonial.hasProjectLink && testimonial.projectLink && (
              <div className="flex-shrink-0">
                <Button
                  href={testimonial.projectLink}
                  variant="primary"
                  size="md"
                >
                  Check the project
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestimonialModal;

